<template>
  <div
    class="cs-router-view"
    :class="{ 'slide-leave-immediately': leavesImmediately }"
    @click="click"
  >
    <div class="cs-router-page">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component ref="default" :is="Component" :key="route.path" />
        </transition>
      </router-view>
    </div>
    <!-- Apparently v-for in this case starts at 1, not 0. -->
    <SlidingRouterView
      v-for="i in slidingLayerCount"
      :key="'sliding' + i"
      :ref="(el) => setDynamicRef(el, i)"
      :name="'sliding' + i"
    />
    <transition name="fade" mode="out-in">
      <JyreFooter v-if="showFooter" :key="route.path" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, ref } from "vue";
import { debug as d } from "debug";
import { CustomRouter, slidingLayerCount } from "./router";
import JyreFooter from "../components/jyre-footer.vue";

import SwipeNavigationInfo from "../components/swipe-navigation-info";
import SlidingRouterView from "../components/sliding-router-view.vue";

const debug = d("cs:router-view");

const route = useRoute();
const router = useRouter();

const props = withDefaults(defineProps<{ name?: string }>(), {
  name: "default",
});

const slidingLayerRefs = ref<InstanceType<typeof SlidingRouterView>[]>([]);

function setDynamicRef(el, index) {
  if (el) {
    slidingLayerRefs.value[index - 1] = el;
    // refs.value[index] = el;
  }
}

const leavesImmediately = ref(false);

const showFooter = computed(() => {
  return !(route.meta?.hideFooter ?? false);
});

function back() {
  SwipeNavigationInfo.temporarilyDisable();
  window.history.back();
}

// https://stackoverflow.com/questions/12551920/capturing-all-the-a-click-event
// find first parent with tagName [tagname]
function findParent(tagname: string, initialElement: Element | null) {
  let el = initialElement;
  while (el) {
    const name = el.nodeName || el.tagName;
    if (name && name.toLowerCase() === tagname.toLowerCase()) {
      return el;
    }
    el = el.parentNode as Element;
  }
  return null;
}

function click(event: MouseEvent | KeyboardEvent) {
  const e = event || window.event;
  const ele = findParent("a", (e.target || e.srcElement) as Element);
  if (ele && ele.getAttribute && !ele.getAttribute("target")) {
    const href = ele.getAttribute("href");
    (router as CustomRouter).handleClick(href, e as MouseEvent);
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../styles/bootstrap/variables.less";

.cs-router-page {
  min-height: 100vh;
}

.routing-forward-enter-active,
.routing-back-leave-active {
  transition: transform 1s ease-out;
  height: 100vh;
  position: fixed;
  width: 100vw;
  top: 0;
  left: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 1050;
  box-shadow: 2px 13px 13px #888;
  background: white;
  margin-top: 50px;
}

.routing-back-enter {
  visibility: hidden;
}

.routing-forward-leave-active {
  display: none;
}

.routing-forward-enter,
.routing-back-leave-to {
  transform: translateX(100%);
}

html.body-scroll-locked,
html.body-scroll-locked body {
  position: relative;
  min-height: 100%;
  overflow: hidden;
}

.slide-leave-immediately {
  .routing-forward-leave,
  .routing-forward-leave-to,
  .routing-forward-leave-active {
    visibility: hidden;
    transform: translateX(100%);
    transition: none;
  }

  .routing-forward-enter,
  .routing-forward-enter-active {
    transform: none;
    transition: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
</style>
